import { useCallback, useEffect, useState } from 'react'
import { creerBrouillon, enregistrerBrouillon, listerModeles, publierModele, type ChampsModele, type ModeleContrat } from '../../api/onboarding'
import { diagnostiquerModele, messageErreur, VARIABLES_CONTRAT } from '../../api/onboardingRegles'
import { dateCourte } from '../Format'
import { Modale } from '../Modale'
import { Erreur, Info, MarkdownRendu, useAction } from './Briques'

const NOUVEAU_TEXTE = '**ENTRE LES SOUSSIGNÉS**\n\nRédigez ici le contrat en markdown. Variables : {{raison_sociale}}, {{gerant}}…\n\n## Article 1 — Objet\n\n- point un\n- point deux\n'

/**
 * « Modèles de contrat » — RÉSERVÉ AU PROPRIÉTAIRE (les policies de la base aussi). Versions, brouillons
 * éditables, publication. Une version publiée est IMMUABLE : l'édition est désactivée, on crée une nouvelle
 * version. Tant que « texte provisoire » est coché, la base REFUSE de publier. Le texte accepte des
 * variables `{{…}}` remplacées au moment de l'envoi ; une marque inconnue bloquerait chaque envoi, d'où
 * l'alerte avant publication.
 */
export function ModelesContrat({ ouvert, surFermer }: { ouvert: boolean; surFermer: () => void }) {
  const [modeles, setModeles] = useState<ModeleContrat[]>([])
  const [erreurLecture, setErreurLecture] = useState<string | null>(null)
  const [choisiId, setChoisiId] = useState<string | null>(null)
  const creation = useAction()

  const charger = useCallback(async (selection?: string) => {
    try {
      setModeles(await listerModeles())
      setErreurLecture(null)
      if (selection) setChoisiId(selection)
    } catch (e) {
      setErreurLecture(messageErreur(e))
    }
  }, [])

  useEffect(() => { if (ouvert) void charger() }, [ouvert, charger])

  const choisi = modeles.find((m) => m.id === choisiId) ?? null
  const dernier = modeles[0] ?? null

  function nouveau() {
    void creation.lancer(async () => {
      const champs: ChampsModele = dernier
        ? { titre: dernier.titre, corps: dernier.corps, provisoire: dernier.provisoire }
        : { titre: 'Contrat de prestation de livraison opérée — client PME', corps: NOUVEAU_TEXTE, provisoire: true }
      const m = await creerBrouillon(champs)
      await charger(m.id)
    })
  }

  return (
    <Modale titre="Modèles de contrat — client PME" ouverte={ouvert} surFermer={surFermer}>
      <div className="onb-large">
        <div className="onb-alerte" style={{ marginTop: 0 }}>
          Le contrat livré avec l’application est un <b>brouillon PROVISOIRE</b> à relire et compléter (plafond d’indemnisation, durée, délais…).
          Tant que « texte provisoire » est coché, la publication est refusée. Une version publiée ne se modifie plus : on en crée une nouvelle.
        </div>
        <Erreur texte={erreurLecture} />
        <div className="onb-mod-grille">
          <div>
            <button className="btn btn-outline btn-sm" disabled={creation.occupe} onClick={nouveau}
              title="Nouvelle version = dernière version + 1, copiée de la dernière">+ Nouveau brouillon</button>
            <Erreur texte={creation.erreur} />
            <div style={{ marginTop: 10 }}>
              {modeles.map((m) => (
                <button key={m.id} className={`onb-mod-item${m.id === choisiId ? ' on' : ''}`} onClick={() => setChoisiId(m.id)}>
                  <b>Version {m.version}</b> — {m.titre}
                  <span className="sous">
                    {m.publieLe ? <span className="badge b-green">Publiée {dateCourte(m.publieLe)}</span> : <span className="badge b-warn">Brouillon</span>}
                    {m.provisoire && <span className="badge b-red">Provisoire</span>}
                  </span>
                </button>
              ))}
              {modeles.length === 0 && <p className="onb-note">Aucune version.</p>}
            </div>
          </div>
          <div>
            {choisi
              ? <EditeurModele key={choisi.id} modele={choisi} surChange={() => void charger(choisi.id)} />
              : <p className="onb-note">Choisissez une version à gauche, ou créez un brouillon.</p>}
          </div>
        </div>
      </div>
    </Modale>
  )
}

function EditeurModele({ modele, surChange }: { modele: ModeleContrat; surChange: () => void }) {
  const publie = modele.publieLe !== null
  const [titre, setTitre] = useState(modele.titre)
  const [corps, setCorps] = useState(modele.corps)
  const [provisoire, setProvisoire] = useState(modele.provisoire)
  const [apercu, setApercu] = useState(publie)
  const { occupe, erreur, info, lancer, signaler } = useAction()
  const diag = diagnostiquerModele(corps)
  const champs = (): ChampsModele => ({ titre, corps, provisoire })

  function enregistrer() {
    if (!titre.trim()) { signaler('Le titre est obligatoire.'); return }
    void lancer(async () => { await enregistrerBrouillon(modele.id, champs()); surChange(); return 'Brouillon enregistré.' })
  }

  function publier() {
    if (provisoire) { signaler('Ce contrat est marqué « texte provisoire » : remplacez le texte, puis décochez la case avant de publier.'); return }
    if (!titre.trim()) { signaler('Le titre est obligatoire.'); return }
    if (diag.marquesInconnues.length) {
      signaler(`Marque(s) inconnue(s) : ${diag.marquesInconnues.map((v) => `{{${v}}}`).join(', ')}. Elles bloqueraient chaque envoi : corrigez-les d’abord.`)
      return
    }
    const alerte = diag.passagesACompleter > 0 ? `\n\nAttention : ${diag.passagesACompleter} passage(s) entre crochets […] subsistent dans le texte.` : ''
    if (!window.confirm(`Publier la version ${modele.version} ?\n\nElle deviendra le contrat envoyé à la signature et ne pourra plus être modifiée.${alerte}`)) return
    void lancer(async () => { await publierModele(modele.id, champs()); surChange(); return 'Version publiée : c’est désormais le contrat envoyé à la signature.' })
  }

  return (
    <>
      <div className="onb-actions" style={{ marginTop: 0, marginBottom: 8 }}>
        <b>Version {modele.version}</b>
        {publie ? <span className="badge b-green">Publiée le {dateCourte(modele.publieLe)}</span> : <span className="badge b-warn">Brouillon</span>}
      </div>
      {publie && <p className="onb-note">Une version publiée n’est plus modifiable : créez un nouveau brouillon pour la faire évoluer.</p>}

      <div className="field">
        <label>Titre</label>
        <input value={titre} disabled={publie} onChange={(e) => setTitre(e.target.value)} />
      </div>
      <label className="onb-coche" style={{ margin: '6px 0' }}>
        <input type="checkbox" checked={provisoire} disabled={publie} onChange={(e) => setProvisoire(e.target.checked)} />
        Texte provisoire (empêche la publication)
      </label>

      {!publie && (
        <p className="onb-note">
          Variables disponibles : {VARIABLES_CONTRAT.map((v) => `{{${v}}}`).join(' ')}
        </p>
      )}
      {diag.marquesInconnues.length > 0 && !publie && (
        <div className="onb-alerte">Marque(s) inconnue(s) : {diag.marquesInconnues.map((v) => `{{${v}}}`).join(', ')} — l’envoi du contrat serait refusé.</div>
      )}
      {diag.marquesSocieteSeulement.length > 0 && !publie && (
        <div className="onb-alerte">
          {diag.marquesSocieteSeulement.map((v) => `{{${v}}}`).join(', ')} n’existe que pour une société : l’envoi à une personne physique ou à un entreprenant serait refusé.
          Pour désigner le client, préférez {'{{designation_partie}}'} (adaptée à son statut juridique) et {'{{nom_partie}}'}.
        </div>
      )}
      {diag.passagesACompleter > 0 && !publie && (
        <div className="onb-alerte">{diag.passagesACompleter} passage(s) entre crochets […] à compléter avant publication.</div>
      )}

      <div className="onb-chips">
        <button className={`onb-chip${!apercu ? ' on' : ''}`} disabled={publie} onClick={() => setApercu(false)}>Édition</button>
        <button className={`onb-chip${apercu ? ' on' : ''}`} onClick={() => setApercu(true)}>Aperçu</button>
      </div>
      {apercu ? (
        <div className="onb-md-box"><MarkdownRendu source={corps} /></div>
      ) : (
        <textarea className="onb-modele-texte" value={corps} disabled={publie} onChange={(e) => setCorps(e.target.value)} spellCheck />
      )}

      {!publie && (
        <div className="onb-actions">
          <button className="btn btn-outline btn-sm" disabled={occupe} onClick={enregistrer}>Enregistrer le brouillon</button>
          <button className="btn btn-gold btn-sm" disabled={occupe} onClick={publier}>Publier</button>
        </div>
      )}
      <Erreur texte={erreur} />
      <Info texte={info} />
    </>
  )
}
